import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from '@/shared/components/Input';
import { Select } from '@/shared/components/Select';
import { Textarea } from '@/shared/components/Textarea';
import { Button } from '@/shared/components/Button';
import { ApiError } from '@/lib/api-client';
import { MONTH_OPTIONS } from '@/shared/utils/format';
import { useOppositions } from '@/features/director-professors/hooks/useSubjects';
import { useShifts } from '../hooks/useShifts';
import { useCreateSchedule } from '../hooks/useSchedules';
/** Sentinel for "common to all shifts" in the shift select → shift_id null. */
const ALL_SHIFTS = '__all__';
export function CreateScheduleForm({ onSuccess, defaultMonth, defaultYear }) {
    const [serverError, setServerError] = useState('');
    const createSchedule = useCreateSchedule();
    const { data: oppositions = [], isLoading: oppLoading } = useOppositions();
    const enabledOppositions = oppositions.filter((o) => o.is_enabled);
    const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
        defaultValues: {
            opposition_id: '',
            shift_id: '',
            month: defaultMonth ?? new Date().getMonth() + 1,
            year: defaultYear ?? new Date().getFullYear(),
            notes: '',
        },
    });
    const oppositionId = watch('opposition_id');
    const shiftId = watch('shift_id');
    const { data: shifts = [], isLoading: shiftsLoading } = useShifts(oppositionId || null);
    useEffect(() => {
        const first = enabledOppositions[0];
        if (!oppositionId && first)
            setValue('opposition_id', first.id);
    }, [enabledOppositions, oppositionId, setValue]);
    useEffect(() => {
        setValue('shift_id', '');
    }, [oppositionId, setValue]);
    function onSubmit(values) {
        setServerError('');
        if (!values.opposition_id || !values.shift_id) {
            setServerError('Selecciona oposición y turno.');
            return;
        }
        const dto = {
            opposition_id: values.opposition_id,
            // ALL_SHIFTS ⇒ horario común a todos los turnos (shift_id null).
            shift_id: values.shift_id === ALL_SHIFTS ? null : values.shift_id,
            month: Number(values.month),
            year: Number(values.year),
            ...(values.notes.trim() ? { notes: values.notes.trim() } : {}),
        };
        createSchedule.mutate(dto, {
            onSuccess: () => onSuccess(),
            onError: (err) => {
                setServerError(err instanceof ApiError ? err.message : 'Ha ocurrido un error.');
            },
        });
    }
    const shiftPlaceholder = !oppositionId
        ? 'Selecciona oposición primero'
        : shiftsLoading
            ? 'Cargando…'
            : shifts.length === 0
                ? 'No hay turnos en esta oposición'
                : 'Seleccionar turno';
    return (_jsxs("form", { onSubmit: handleSubmit(onSubmit), className: "flex flex-col gap-4", noValidate: true, children: [_jsx(Select, { label: "Oposici\u00F3n", disabled: oppLoading, options: [
                    { value: '', label: oppLoading ? 'Cargando…' : 'Seleccionar oposición' },
                    ...enabledOppositions.map((o) => ({ value: o.id, label: o.name })),
                ], error: errors.opposition_id?.message, ...register('opposition_id', { required: 'La oposición es obligatoria' }) }), _jsx(Select, { label: "Turno", disabled: !oppositionId || shiftsLoading, options: [
                    { value: '', label: shiftPlaceholder },
                    ...(oppositionId && !shiftsLoading
                        ? [{ value: ALL_SHIFTS, label: 'Todos los turnos (común, p. ej. psicotécnicos)' }]
                        : []),
                    ...shifts.map((s) => ({ value: s.id, label: s.name })),
                ], error: errors.shift_id?.message, ...register('shift_id', { required: 'El turno es obligatorio' }) }), _jsxs("div", { className: "grid gap-4 sm:grid-cols-2", children: [_jsx(Select, { label: "Mes", options: MONTH_OPTIONS, ...register('month', { valueAsNumber: true }) }), _jsx(Input, { label: "A\u00F1o", type: "number", error: errors.year?.message, ...register('year', {
                            required: 'El año es obligatorio',
                            valueAsNumber: true,
                        }) })] }), _jsx(Textarea, { label: "Notas (opcional)", rows: 3, ...register('notes') }), serverError && (_jsx("p", { role: "alert", className: "text-sm text-red-600 dark:text-red-400", children: serverError })), _jsx(Button, { type: "submit", loading: createSchedule.isPending, disabled: !oppositionId || !shiftId, className: "w-full", children: "Crear horario" })] }));
}
//# sourceMappingURL=CreateScheduleForm.js.map